import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Package, LogOut } from "lucide-react";
import { shopApi, shopperAuth } from "../lib/shop-api";
import { money, dateShort } from "../lib/api";

export default function AccountPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [authed, setAuthed] = useState(shopperAuth.isAuthed());
  const [mode, setMode] = useState("login"); // or register
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authed) {
      shopApi.myOrders().then(setOrders).catch(() => {});
    }
  }, [authed]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fn = mode === "login" ? shopApi.login : shopApi.register;
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const r = await fn(payload);
      shopperAuth.save(r.access_token, r.shopper);
      setAuthed(true);
      toast.success(mode === "login" ? "Sesión iniciada" : "Cuenta creada");
      const redirect = sp.get("redirect");
      if (redirect) nav(redirect);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    shopperAuth.clear();
    setAuthed(false);
    setOrders([]);
    toast.success("Sesión cerrada");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0908] text-[#F5F0E6] flex items-center justify-center p-4">
        <div className="w-full max-w-sm bg-[#120F0D] border border-white/10 rounded-2xl p-8">
          <div className="text-center mb-6">
            <User size={36} className="mx-auto text-[#C89B3C] mb-2" />
            <h1 className="text-3xl" style={{ fontFamily: "'Playfair Display'" }}>
              {mode === "login" ? "Iniciar Sesión" : "Crear Cuenta"}
            </h1>
          </div>
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre" className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" required data-testid="shopper-name" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Teléfono" className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" />
              </>
            )}
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Correo" type="email" className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" required data-testid="shopper-email" />
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Contraseña" type="password" className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm" required minLength={6} data-testid="shopper-password" />
            <button type="submit" disabled={busy} className="w-full bg-[#C89B3C] text-[#0A0908] font-bold py-2.5 rounded-full disabled:opacity-60" data-testid="shopper-submit">
              {busy ? "Un momento…" : mode === "login" ? "Entrar" : "Crear cuenta"}
            </button>
          </form>
          <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full mt-4 text-sm text-[#C89B3C] hover:text-[#E6BF5F]" data-testid="shopper-toggle-mode">
            {mode === "login" ? "¿Nuevo? Crear cuenta" : "¿Ya tienes cuenta? Entrar"}
          </button>
        </div>
      </div>
    );
  }

  const info = shopperAuth.info();
  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F5F0E6]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#C89B3C] mb-2">Mi Cuenta</p>
            <h1 className="text-4xl" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }}>Hola, {info?.name || info?.email}</h1>
          </div>
          <button onClick={logout} className="flex items-center gap-1 text-sm text-[#F5F0E6]/70 hover:text-red-400" data-testid="shopper-logout">
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>

        <section className="bg-[#120F0D] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display'" }}>
            <Package size={20} className="text-[#C89B3C]" /> Mis pedidos
          </h2>
          {orders.length === 0 ? (
            <p className="text-sm text-[#F5F0E6]/60">Aún no tienes pedidos. <Link to="/shop" className="text-[#C89B3C] hover:underline">Comprar ahora</Link>.</p>
          ) : (
            <ul className="divide-y divide-white/10" data-testid="my-orders-list">
              {orders.map((o) => (
                <li key={o.id} className="py-4 flex items-center justify-between gap-4" data-testid={`order-row-${o.order_number}`}>
                  <div>
                    <p className="font-mono text-sm text-[#C89B3C]">{o.order_number}</p>
                    <p className="text-xs text-[#F5F0E6]/60">{dateShort(o.created_at)} · {o.items.length} artículo(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{money(o.total)}</p>
                    <p className="text-xs uppercase tracking-wider text-[#F5F0E6]/70">{o.status}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
